import { ref, watch, inject, provide } from 'vue'
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging'

import { authUser } from 'src/composables/app/useAuth'
import { fcmStatus, notificationToken } from 'src/composables/fcm/fcmToken.state'

import { getFirebaseApp } from 'src/services/app/firebase.app'
import { log } from 'src/services/app/log.service'
import { updateToken } from 'src/services/api/firebase.service'
import { getNotificationPermission } from 'src/services/helpers/fcm.helper'

export { notificationToken }

const FCM_KEY = Symbol('fcm')

const isInitialized = ref(false)
const messageSubscribers = new Set()
let initPromise = null
let messaging = null
let unsubscribeMessageListeners = null

/**
 * Self-providing singleton-per-tree (useGraphConfig pattern).
 * Transport only: token init + onMessage bus. Message routing lives elsewhere.
 */
export function useFCM() {
  const existing = inject(FCM_KEY, null)
  if (existing) return existing

  watch(
    () => authUser.value?.id,
    async (hasUser) => {
      if (!hasUser) {
        isInitialized.value = false
        initPromise = null
        return
      }
      if (!isInitialized.value) {
        log.info('[FCM] Auth ready, initializing...')
        await initialize()
      }
    },
    { immediate: true },
  )

  async function initialize() {
    if (isInitialized.value || fcmStatus.value.isProcessing) return
    await runInitialize()
  }

  function subscribeToMessages(callback) {
    if (typeof callback !== 'function') {
      log.error('[FCM] Subscribe callback must be a function')
      return () => {}
    }

    messageSubscribers.add(callback)

    return () => {
      messageSubscribers.delete(callback)
    }
  }

  const api = {
    status: fcmStatus,
    isInitialized,
    initialize,
    resendToken,
    subscribeToMessages,
  }
  provide(FCM_KEY, api)
  return api
}

/** For legacy topic subscribe outside setup — no composable calls. */
export async function ensureFcmReady() {
  await runInitialize()
  return fcmStatus.value.token
}

function setupMessageListener() {
  if (!messaging) return

  if (unsubscribeMessageListeners) {
    unsubscribeMessageListeners()
  }

  unsubscribeMessageListeners = onMessage(messaging, (payload) => {
    messageSubscribers.forEach((callback) => {
      try {
        callback(payload)
      } catch (err) {
        log.error('[FCM] Subscriber callback error:', err)
      }
    })
  })
}

async function runInitialize() {
  if (isInitialized.value) return
  if (initPromise) return initPromise

  initPromise = doInitialize()
  try {
    await initPromise
  } finally {
    if (!isInitialized.value) {
      initPromise = null
    }
  }
}

async function doInitialize() {
  if (isInitialized.value || fcmStatus.value.isProcessing) return

  fcmStatus.value.isProcessing = true
  fcmStatus.value.lastError = null
  log.info('[FCM] Initializing...')

  try {
    fcmStatus.value.isSupported = await isSupported()
    if (!fcmStatus.value.isSupported) {
      log.info('[FCM] Messaging not supported in this browser')
      return
    }

    fcmStatus.value.permission = getNotificationPermission()
    if (fcmStatus.value.permission === 'default') {
      fcmStatus.value.permission = await Notification.requestPermission()
    }
    if (fcmStatus.value.permission !== 'granted') {
      log.info(`[FCM] Permission: ${fcmStatus.value.permission}`)
      return
    }

    if (!messaging) {
      messaging = getMessaging(getFirebaseApp())
    }

    const token = await getToken(messaging, { vapidKey: process.env.VITE_FIREBASE_VAPID_KEY })
    if (!token) throw new Error('Failed to get FCM token')

    fcmStatus.value.token = token
    fcmStatus.value.tokenStatus = 'fetched'
    log.info('[FCM] Token:', token.substring(0, 20) + '...')

    await syncTokenWithBackend(token)

    setupMessageListener()
    isInitialized.value = true
    log.info('[FCM] Initialized')
  } catch (err) {
    log.error('[FCM] Initialization failed:', err)
    fcmStatus.value.lastError = err
    fcmStatus.value.tokenStatus = 'error'
  } finally {
    fcmStatus.value.isProcessing = false
    fcmStatus.value.firstInit = true
  }
}

async function syncTokenWithBackend(token) {
  const response = await updateToken(token)
  fcmStatus.value.tokenStatus = response?.cached ? 'cached-valid' : 'sent-to-backend'
  return response
}

async function resendToken() {
  const token = fcmStatus.value.token
  if (!token || fcmStatus.value.isProcessing) return false

  fcmStatus.value.isProcessing = true
  fcmStatus.value.lastError = null

  try {
    await syncTokenWithBackend(token)
    return true
  } catch (err) {
    log.error('[FCM] Token resend failed:', err)
    fcmStatus.value.lastError = err
    fcmStatus.value.tokenStatus = 'error'
    return false
  } finally {
    fcmStatus.value.isProcessing = false
  }
}
